import { useEffect, useLayoutEffect, useSyncExternalStore, type ReactNode } from 'react';
import { useIsRestoring, useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { useTRPC } from '../../lib/trpc';
import { accountKey } from '../../lib/cache-account';
import { accountRef, type AccountRef } from '../../lib/account';
import {
  confirmAnsweredSession,
  useCacheAccount,
  useIdentity,
  useOtherTabs,
  type Identity,
} from '../../lib/identity';
import type { Stopped } from '../../lib/identity-tabs';
import { useDeviceOffline } from '../ui/useConnection';
import { sessionQueryOptions } from '../../lib/session-query';
import { decideGate } from './gate';
import { AuthScreen } from './AuthScreen';
import { CreateOrgScreen } from './CreateOrgScreen';
import { SessionProvider } from '../session';
import { uploadQueue } from '../../lib/upload-queue';
import { failedWriteLog } from '../../lib/failed-writes';
import { PageSkeleton } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import { Button } from '../ui/Button';
import { IconTeam, IconWrenchBig } from '../icons';

/**
 * Paths the gate steps aside for, because everything it does below is wrong for the person
 * arriving on one.
 *
 * `/join/` — an invited hire owns its own auth. They are usually signed out (they would get
 * the generic sign-in form instead of "Smoke Mechanical invited you"), and the moment they
 * make an account they belong to no org, which would drop them on "create your shop" — the
 * one place a new hire must never end up.
 *
 * `/i/` and `/q/` — a customer looking at their own invoice, or deciding on a quote. They have no account and never will. This
 * one was added after watching the gate serve a shop's customer a Servwell SIGN-UP FORM
 * instead of the bill they had been sent, which is the single most alienating thing this
 * product could do to somebody else's customer.
 *
 * Keep it beside `isPublicPath` and the route tree — the guard test pins all three together,
 * because a hand-written list of "which pages are public" is exactly the thing that rots the
 * next time somebody adds one.
 */
export const UNGATED_PREFIXES = ['/join/', '/i/', '/q/'] as const;

/**
 * Public pages whose path carries no trailing segment, so prefix matching is the wrong test.
 *
 * `/sign-in` is where Better Auth's OAuth provider sends an unauthenticated authorization —
 * it IS the sign-in screen, so gating it behind the sign-in screen is a loop. Matched exactly
 * because `startsWith('/sign-in')` would also wave through `/sign-in-anything-at-all`.
 */
export const UNGATED_EXACT = ['/sign-in'] as const;

export function isPublicPath(pathname: string): boolean {
  return (
    UNGATED_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
    (UNGATED_EXACT as readonly string[]).includes(pathname)
  );
}

/**
 * Auth gate at the router root: sign in → create shop → app. UI role gating is
 * cosmetic — the server's permission matrix is the real gate.
 *
 * Law (data honesty, at the front door): only a definitive `authenticated: false`
 * from the server shows the sign-in screen. A transport failure or an offline-paused
 * query must never masquerade as "signed out" — that would strand a signed-in tech
 * on a login form in a basement. Hence isPending (covers paused) for the skeleton
 * and an explicit error state with a retry.
 *
 * And (MAP 225a) the saved cache is somebody's. Online, nothing of a shop's shows until the
 * network has said who is signed in and the cache, and the disk, hold no one else's
 * queries; offline, the saved account's screens show from cache. The decision itself is
 * `decideGate` in gate.ts.
 *
 * 🔴 `/consent` IS FULLY GATED, AND AN EARLIER VERSION OF THIS FILE WAS WRONG ABOUT WHY NOT.
 *
 * It used to bypass the org and role checks, on the reasoning that diverting an owner
 * mid-authorization would abandon a connector still waiting on them. That reasoning does not
 * survive contact with `CreateOrgScreen`: the gate renders it IN PLACE, without navigating, so
 * the signed OAuth query stays in the URL and the flow resumes by itself the moment a shop
 * exists. Nothing was ever at risk of being lost.
 *
 * What the bypass DID do was ship a broken path. The consent screen offers "Create one", so a
 * brand-new account can be made mid-authorization — and that account belongs to no shop. With
 * the org check skipped they could approve, receive a real token, and then have every `/mcp`
 * call 401 forever, because `resolveMcpSession` needs a `member` row that does not exist. A
 * connector that authorizes successfully and then never works is worse than one that refuses.
 *
 * So there is no third category. `/consent` is simply not public, and the ordinary gate — sign
 * in, then have a shop, then have a role — is exactly the sequence consent requires anyway.
 * (Codex adversary, round 3, #2.)
 */
export function RootGate({ children }: { children: ReactNode }) {
  const trpc = useTRPC();
  const session = useQuery(sessionQueryOptions(trpc));
  /**
   * This MUST come from router state, not `window.location`. Reading the path imperatively
   * works on first paint and then silently rots: this component has no other reason to
   * re-render, so after a client-side navigation OFF /join it kept returning the bypass
   * while the Outlet below it swapped in a page that needs the session — which crashed on
   * a missing provider, at the exact moment a new hire finished signing up.
   */
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // `navigator.onLine === false`, through TanStack's onlineManager (MAP 225b). Offline,
  // `me.session` pauses rather than failing, and the signal's return continues it: no recheck
  // is needed, and no error state exists to flash in between.
  const offline = useDeviceOffline();
  const restoring = useIsRestoring();
  const identity = useIdentity();
  const cache = useCacheAccount(identity);
  const tabs = useOtherTabs(identity);

  /**
   * Every way of changing who is signed in ends HERE (MAP 225a): sign-in, sign-up, a join,
   * a new shop, an OAuth return, and a shop or role changed from anywhere
   * else. Each one ends in a `me.session` answer, and every answer is checked against the
   * saved cache's account.
   */
  useEffect(() => {
    void confirmAnsweredSession(identity, session.data, tabs);
  }, [identity, session.data, tabs]);

  const gate = decideGate({
    isPublic: isPublicPath(pathname),
    offline,
    restoring,
    session,
    cache,
    tabs,
  });

  // Other tabs judge this one by what it DRAWS, never by its restored label. And this tab
  // WRITES as what it draws: the confirmed account online, the saved account offline, nobody
  // otherwise. `confirmedAt` is the network answer that confirmed it (null: the saved account,
  // drawn offline), so every confirmation clears a 401's suspension, and writes made after an
  // offline start wait for one.
  const drawn = gate.kind === 'shop' ? accountKey(gate.session) : null;
  const writesAs = gate.kind === 'shop' ? accountRef(gate.session) : null;
  const confirmedAt =
    gate.kind === 'shop' && session.data !== undefined ? session.dataUpdatedAt : null;
  useLayoutEffect(() => {
    identity.tabs.drawing(drawn);
    identity.writes.setAccount(writesAs, confirmedAt);
    // `drawn` names the same person and shop as `writesAs`.
  }, [identity, drawn, confirmedAt]);
  // The shop draws only once that account's queued writes are back in the cache: the layout
  // effect above builds them, and this re-renders before paint once it has, so a screen's
  // guard on a queued write (Take payment) reads it from the screen's very first render.
  const writes = useWritesState(identity, writesAs);

  switch (gate.kind) {
    case 'public':
      return <>{children}</>;
    case 'stopped':
      return <OtherTabState reason={gate.reason} />;
    case 'cannot-clear':
      return (
        <CenteredState>
          <EmptyState
            icon={IconWrenchBig}
            title="Couldn't clear the previous account's data on this device"
            description="Servwell won't show anything here until it has. Free up some storage on this device, then try again."
            action={
              <Button onClick={() => void identity.cacheAccount.retry()}>
                Try again
              </Button>
            }
          />
        </CenteredState>
      );
    case 'loading':
      return <PageSkeleton />;
    case 'unreachable':
      return (
        <CenteredState>
          <EmptyState
            icon={IconWrenchBig}
            title="Couldn't reach Servwell"
            description="We couldn't confirm your sign-in. Check your connection and try again."
            action={<Button onClick={() => void session.refetch()}>Try again</Button>}
          />
        </CenteredState>
      );
    case 'sign-in':
      return <AuthScreen />;
    case 'create-shop':
      return <CreateOrgScreen />;
    case 'no-role':
      return (
        <CenteredState>
          <EmptyState
            icon={IconWrenchBig}
            title="Couldn't confirm your access"
            description="Your account isn't showing a role in this shop. Try again, or ask the owner to re-invite you."
            action={<Button onClick={() => void session.refetch()}>Try again</Button>}
          />
        </CenteredState>
      );
    case 'shop':
      // Never over a queue this tab has not read: a second write could be made over one it
      // cannot see (a payment, a part, a van transfer). The read is local and quick; a
      // device that cannot answer it gets told so, and nothing of the shop is drawn.
      if (writes === 'unreadable') {
        return (
          <CenteredState>
            <EmptyState
              icon={IconWrenchBig}
              title="Couldn't read the changes saved on this device."
              description="Servwell won't show anything here until it has, so nothing you did is sent twice. Try again; if it keeps happening, close Servwell and open it again."
              action={
                <Button onClick={() => identity.writes.retryRead()}>Try again</Button>
              }
            />
          </CenteredState>
        );
      }
      if (writes === 'reading') return <PageSkeleton />;
      // The confirmed account online, or the saved account offline: the same one hands the
      // upload queue its account, so photos never drain under anybody else's.
      return (
        <SessionProvider value={gate.session}>
          <OfflineQueueAccount
            userId={gate.session.userId}
            orgId={gate.session.orgId}
            confirmed={confirmedAt !== null}
          />
          {children}
        </SessionProvider>
      );
  }
}

/** Whether `identity.writes` has `account`'s queued writes in the cache yet. */
/** Whether `account`'s queued writes have been read into the cache, are being read, or cannot be. */
function useWritesState(
  identity: Identity,
  account: AccountRef | null,
): 'ready' | 'reading' | 'unreadable' {
  const state = () =>
    identity.writes.readyFor(account)
      ? 'ready'
      : identity.writes.unreadable()
        ? 'unreadable'
        : 'reading';
  return useSyncExternalStore(identity.writes.subscribe, state, state);
}

/**
 * Another tab signed in as someone else, switched shop, or signed out. Everything this tab
 * still holds belongs to the account that WAS signed in, and anything it sent now would go
 * out under the new cookie, so it draws nothing until it is reloaded.
 */
function OtherTabState({ reason }: { reason: Stopped }) {
  return (
    <CenteredState>
      <EmptyState
        icon={IconTeam}
        title={
          reason === 'signed-out'
            ? 'You signed out in another tab'
            : 'You signed in as someone else in another tab'
        }
        description="This tab was still showing the account from before. Reload it to carry on as whoever is signed in now."
        action={<Button onClick={() => location.reload()}>Reload</Button>}
      />
    </CenteredState>
  );
}

/**
 * Hand both offline stores the signed-in account.
 *
 * They are started in `ApiProvider`, above every route and long before anyone is known to be
 * signed in — and both are per-ORIGIN, so a shop tablet two techs use shares them.
 *
 * For **photos** this gates sending: an upload authenticates with whatever cookie exists
 * when it drains, so draining under an unknown identity is how one tech's photograph ends up
 * filed under another's name, or in another org entirely.
 *
 * For **refused writes** it gates showing only. A refusal arrives already owned, handed over
 * from its queued write's row (MAP 225b), so nothing is claimed by whoever signs in here.
 * (Queued writes themselves take the account in the layout effect above, before paint.)
 */
function OfflineQueueAccount({
  userId,
  orgId,
  confirmed,
}: {
  userId: string;
  orgId: string;
  /** The network confirmed this account: photos may go (after an offline start, not yet). */
  confirmed: boolean;
}) {
  useEffect(() => {
    uploadQueue.setAccount({ userId, orgId }, confirmed);
    failedWriteLog.setAccount({ userId, orgId });
    // The boot drain in `startUploadQueue` ran before anyone was known to be signed in and
    // therefore sent nothing. This is the drain that actually goes.
    void uploadQueue.drain();
    return () => {
      uploadQueue.setAccount(null);
      failedWriteLog.setAccount(null);
    };
  }, [userId, orgId, confirmed]);
  return null;
}

function CenteredState({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}
